import { BUSINESS_TIMEZONE } from '@omecare/utils';
import { formatDistanceToNow } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { CheckIcon, CopyIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDateFnsLocale } from '@/hooks/use-date-fns-locale';
import { TONES } from '@/lib/tones';
import { cn } from '@/lib/utils';

/** How long the copy button stays in its "copied" state. */
const COPIED_FEEDBACK_MS = 2000;

/** Characters kept at each end of the shortened identifier. */
const ID_EDGE_LENGTH = 4;

/** Pattern of the full timestamp shown when hovering the relative date. */
const FULL_TIMESTAMP_PATTERN = 'PPpp';

/**
 * Shortens an identifier to its two ends, as the mockup does — the full value
 * is still what gets copied.
 *
 * @param id - the identifier to shorten
 * @returns the shortened identifier, or the original when already short
 */
const shortenId = (id: string): string => {
  if (id.length <= ID_EDGE_LENGTH * 2) {
    return id;
  }
  return `${id.slice(0, ID_EDGE_LENGTH)}…${id.slice(-ID_EDGE_LENGTH)}`;
};

/**
 * Secondary header row of a slot sheet: a copyable slot identifier on the left
 * and how long ago the slot was last modified on the right.
 *
 * @param props.id - the slot identifier, copied in full
 * @param props.updatedAt - when the slot was last modified
 * @returns the meta row element
 */
export const SlotSheetMetaRow = ({
  id,
  updatedAt,
}: {
  id: string;
  updatedAt: Date | null;
}) => {
  const { t } = useTranslation();
  const locale = useDateFnsLocale();
  const [copied, setCopied] = useState(false);

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    } catch {
      // Clipboard access can be denied (insecure context, permission): the id
      // stays visible in the button, so there is nothing to recover from.
    }
  };

  return (
    <div className="flex items-center justify-between gap-2 text-muted-foreground text-xs">
      <button
        type="button"
        onClick={copyId}
        title={t('slot-sheet.copy-id')}
        aria-label={t('slot-sheet.copy-id')}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md border bg-muted/40 px-2 py-1 font-mono text-[11px]',
          'hover:bg-muted/70',
        )}
      >
        <span className="text-muted-foreground/80">ID</span>
        <span>{shortenId(id)}</span>
        {copied ? (
          <CheckIcon
            className={`size-3 ${TONES.emerald.text}`}
            aria-hidden="true"
          />
        ) : (
          <CopyIcon
            className="size-3"
            aria-hidden="true"
          />
        )}
      </button>

      {updatedAt && (
        <span
          title={formatInTimeZone(
            updatedAt,
            BUSINESS_TIMEZONE,
            FULL_TIMESTAMP_PATTERN,
            { locale },
          )}
        >
          {t('slot-sheet.updated-ago', {
            distance: formatDistanceToNow(updatedAt, {
              locale,
              addSuffix: true,
            }),
          })}
        </span>
      )}
    </div>
  );
};
